import styled, { css } from 'styled-components';
import { hover } from 'src/components/utils';
import { PrimaryButton } from '../sharedStyles';
import type { PlanTab } from './PlansComponent';

export const Columns = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, ${theme.sz.ms});
    align-items: center;
    gap: ${theme.sz.s16};
    width: 100%;
    max-width: ${theme.sz.xxl};
    margin: auto;
    padding-top: ${theme.sz.s8};

    @media (max-width: ${theme.sz.lg}) {
      grid-template-columns: minmax(0, 1fr);
      gap: ${theme.sz.s4};
      margin: 0;
      /* Leaves room for the checkout pinned to the bottom of the screen */
      padding: ${theme.sz.s4} 0 ${theme.sz.s54};
    }
  `,
);

export const Pitch = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.sz.s5};

    @media (max-width: ${theme.sz.lg}) {
      gap: ${theme.sz.s3};
    }
  `,
);

export const Title = styled.h1(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.accent.xl};
    margin: 0;
    color: ${theme.co.neutral10};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.accent.xl};
      font-size: ${theme.sz.s6};
    }
  `,
);

export const Tabs = styled.div(
  ({ theme }) => css`
    display: flex;
    padding: ${theme.sz.s1};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.neutral95};

    @media (max-width: ${theme.sz.lg}) {
      padding: ${theme.sz.s05};
    }
  `,
);

export const Tab = styled.button<{ $tab: PlanTab; $selected: boolean }>(
  ({ theme, $tab, $selected }) => css`
    ${theme.typography.button.l};
    min-width: ${theme.sz.s32};
    padding: ${theme.sz.s2} ${theme.sz.s6};
    border: 0;
    border-radius: ${theme.bo.round};
    background-color: transparent;
    color: ${theme.co.neutral40};
    cursor: pointer;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    ${
      $selected &&
      ($tab === 'pro'
        ? css`
            background-color: ${theme.co.primary20};
            color: ${theme.co.neutral10};
          `
        : css`
            background-color: ${theme.co.neutral10};
            color: ${theme.co.neutral99};
          `)
    }

    ${theme.focusVisible};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xxxs};
      min-width: ${theme.sz.s16};
      padding: ${theme.sz.s05} ${theme.sz.s4};
    }
  `,
);

export const FeaturesHeading = styled.h2(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    margin: 0;
    color: ${theme.co.neutral10};

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const Features = styled.ul(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s4};
    margin: 0;
    padding: 0;
    list-style: none;

    @media (max-width: ${theme.sz.lg}) {
      gap: ${theme.sz.s2};
    }
  `,
);

export const Feature = styled.li(
  ({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.sz.s4};

    @media (max-width: ${theme.sz.lg}) {
      align-items: center;
      gap: ${theme.sz.s3};
    }
  `,
);

export const FeatureIcon = styled.span<{ $variant: 'included' | 'limited' | 'excluded' }>(
  ({ theme, $variant }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${theme.sz.s8};
    height: ${theme.sz.s8};
    border-radius: ${theme.bo.round};
    background-color: ${$variant === 'included' ? theme.co.primary90 : theme.co.neutral95};
    color: ${$variant === 'included' ? theme.co.neutral10 : theme.co.neutral40};
    font-size: ${theme.sz.s4};

    @media (max-width: ${theme.sz.lg}) {
      width: ${theme.sz.s5};
      height: ${theme.sz.s5};
      font-size: ${theme.sz.s3};
    }
  `,
);

export const Dash = styled.span(
  ({ theme }) => css`
    width: ${theme.sz.s2};
    height: ${theme.bo.mediumWidth};
    border-radius: ${theme.bo.round};
    background-color: currentColor;
  `,
);

export const FeatureText = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s05};
  `,
);

export const FeatureTitle = styled.span<{ $muted?: boolean }>(
  ({ theme, $muted }) => css`
    ${theme.typography.heading.desktop.xxs};
    color: ${$muted ? theme.co.neutral40 : theme.co.neutral10};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xs};
    }
  `,
);

export const FeatureDetail = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    color: ${theme.co.neutral40};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.s};
    }
  `,
);

export const PlanCard = styled.section(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s4};
    padding: ${theme.sz.s8};
    border: ${theme.bo.mediumWidth} solid ${theme.co.neutral95};
    border-radius: ${theme.bo.large};
    background-color: ${theme.co.neutral99};

    @media (max-width: ${theme.sz.lg}) {
      padding: 0;
      border: 0;
      background-color: transparent;
    }
  `,
);

export const PlanCardTitle = styled.h2(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    margin: 0;
    color: ${theme.co.neutral10};

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const PlanOptions = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};

    @media (max-width: ${theme.sz.lg}) {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: ${theme.sz.s2};
      /* Room for the badges sitting on the cards' top edge */
      padding-top: ${theme.sz.s3};
    }
  `,
);

export const PlanOption = styled.button<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s4};
    width: 100%;
    padding: ${theme.sz.s4} ${theme.sz.s5};
    border: ${theme.bo.mediumWidth} solid ${$selected ? theme.co.primary20 : theme.co.neutral95};
    border-radius: ${theme.bo.medium};
    background-color: ${$selected ? theme.co.primary95 : theme.co.neutral99};
    text-align: start;
    cursor: pointer;
    transition:
      border-color 0.2s ease,
      background-color 0.2s ease;

    &:hover {
      border-color: ${$selected ? theme.co.primary20 : theme.co.primary60};
    }

    ${theme.focusVisible};

    @media (max-width: ${theme.sz.lg}) {
      position: relative;
      flex-direction: column;
      gap: ${theme.sz.s05};
      padding: ${theme.sz.s5} ${theme.sz.s1} ${theme.sz.s3};
      text-align: center;
    }
  `,
);

export const Radio = styled.span<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    position: relative;
    flex-shrink: 0;
    width: ${theme.sz.s6};
    height: ${theme.sz.s6};
    border: ${theme.bo.mediumWidth} solid ${$selected ? theme.co.primary20 : theme.co.neutral50};
    border-radius: ${theme.bo.round};

    ${
      $selected &&
      css`
        &::after {
          content: '';
          position: absolute;
          inset: ${theme.sz.s1};
          border-radius: ${theme.bo.round};
          background-color: ${theme.co.primary20};
        }
      `
    }

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const PlanText = styled.span(
  ({ theme }) => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${theme.sz.s05};

    @media (max-width: ${theme.sz.lg}) {
      flex: none;
      align-items: center;
    }
  `,
);

export const PlanNameRow = styled.span(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s2};
  `,
);

export const PlanName = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    color: ${theme.co.neutral10};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xxs};
    }
  `,
);

export const Badge = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.mobile.xxxs};
    padding: ${theme.sz.s05} ${theme.sz.s2};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary20};
    color: ${theme.co.neutral10};
    white-space: nowrap;

    @media (max-width: ${theme.sz.lg}) {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  `,
);

export const PlanDetail = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    color: ${theme.co.neutral40};

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const PriceSide = styled.span(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${theme.sz.s05};
    text-align: end;

    @media (max-width: ${theme.sz.lg}) {
      align-items: center;
      text-align: center;
    }
  `,
);

export const Price = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    color: ${theme.co.neutral10};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.s};
    }
  `,
);

export const WasPrice = styled.s(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    color: ${theme.co.neutral40};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.s};
    }
  `,
);

export const PriceNote = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    color: ${theme.co.neutral40};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.s};
    }
  `,
);

export const Actions = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s4};
    padding-top: ${theme.sz.s2};

    @media (max-width: ${theme.sz.lg}) {
      gap: ${theme.sz.s2};
      padding-top: 0;
    }
  `,
);

export const SubscribeButton = styled(PrimaryButton)(
  ({ theme }) => css`
    max-width: none;

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.button.s};
      min-height: ${theme.sz.s12};
    }
  `,
);

export const FreeButton = styled.button(
  ({ theme }) => css`
    ${theme.typography.button.l};
    width: 100%;
    min-height: ${theme.sz.s14};
    padding: ${theme.sz.s3} ${theme.sz.s6};
    border: ${theme.bo.mediumWidth} solid ${theme.co.neutral10};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.neutral99};
    color: ${theme.co.neutral10};
    cursor: pointer;

    &:hover {
      background-color: ${hover(theme.co.neutral99)};
    }

    ${theme.focusVisible};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.button.s};
      min-height: ${theme.sz.s12};
    }
  `,
);

export const ErrorText = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    margin: 0;
    color: ${theme.co.error40};
    text-align: center;
  `,
);

export const Checkout = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s4};

    @media (max-width: ${theme.sz.lg}) {
      position: fixed;
      inset-inline: 0;
      bottom: 0;
      z-index: ${theme.zi.z1};
      gap: ${theme.sz.s2};
      padding: ${theme.sz.s3} ${theme.sz.s5} calc(${theme.sz.s3} + env(safe-area-inset-bottom));
      border-top: ${theme.bo.thinWidth} solid ${theme.co.neutral95};
      background-color: ${theme.co.blobBackground};
    }
  `,
);

export const Legal = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    margin: 0;
    color: ${theme.co.neutral40};
    text-align: center;
  `,
);

export const LegalLink = styled.a(
  ({ theme }) => css`
    color: inherit;
    text-decoration: underline;

    &:hover {
      color: ${theme.co.neutral10};
    }
  `,
);
